import React, { Component } from 'react';
import PropTypes from 'prop-types';
import copyToClipboard from 'copy-to-clipboard';
import url from 'url';
import isEmail from 'common/util/isEmail';

import {
  makeListingFullAddressString,
} from 'common/listingHelpers';

import {
  USER_ROLE_ADMIN,
} from 'common/userRoles';

import {
  LEASE_DOCUMENT_STATUS_SIGNED,
  LEASE_DOCUMENT_STATUS_VOIDED,
} from 'common/leaseConstants';

import {
  getLeaseDraftingFields,
  getRecipientsList,
} from 'common/leaseDrafting/leaseDraftingFields';

import {
  SCREEN_ID_LEASE_DRAFT_HELPER,
} from 'client/constants/screenIds';

import {
  SR_API_ACTION_LEASE_DRAFT_GET_INFO,
  SR_API_ACTION_LEASE_DRAFT_GET_PARTNER_CONFIGURATION,
  SR_API_ACTION_LEASE_DOCUMENT_CREATE_DRAFT,
  SR_API_ACTION_LEASE_DOCUMENT_REVIEW_DRAFT,
  SR_API_ACTION_LEASE_DOCUMENT_REQUEST_SIGNATURE,
  SR_API_ACTION_LEASE_DOCUMENT_VOID,
} from 'api/actionTypes';

import allowedRoles from 'client/helpers/allowedRoles';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';
import branch from 'client/core/branch';
import postWithState from 'client/actions/postWithState';
import LeaseActions from 'client/actions/Lease';
import LeaseDocumentActions from 'client/actions/LeaseDocument';

import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrLeaseDraftHelper from 'client/components/SrLeaseDraftHelper/SrLeaseDraftHelper';
import SrAdminHouseholdNotes from 'client/components/SrAdminHouseholdNotes/SrAdminHouseholdNotes';
import SrLoadingSpinner from 'client/components/SrLoadingSpinner/SrLoadingSpinner';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_YELLOW,
} from 'client/components/SrButton/SrButton';

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_ADMIN,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_LEASE_DRAFT_HELPER)
@withApiState({
  getDraftingInfoApiState: {
    action: SR_API_ACTION_LEASE_DRAFT_GET_INFO,
  },
  getLeaseDraftConfigApiState: {
    action: SR_API_ACTION_LEASE_DRAFT_GET_PARTNER_CONFIGURATION,
  },
})
@withScrollToTop
@branch(({ match: { params: { householdListingId } } }) => {
  return {
    leaseDraftInfo: ['leases', 'draftInfo', householdListingId],
    leaseDocuments: ['leases', 'documentsByHouseholdListingId', householdListingId],
  };
})
@branch(({ leaseDraftInfo }) => {
  const cursors = {
    currentUser: ['currentUser'],
    appUrl: ['env', 'APP_URL'],
  };

  if (leaseDraftInfo) {
    cursors.partnerLeaseDraftConfiguration = [
      'leases',
      'configurationsByPartnerId',
      leaseDraftInfo.partner.id,
    ];
  }

  return cursors;
})
class AdminLeaseDraftHelperPage extends Component {
  static propTypes = {
    leaseDraftInfo: PropTypes.object,
    leaseDocuments: PropTypes.array,
    currentUser: PropTypes.object.isRequired,
    getDraftingInfoApiState: PropTypes.object.isRequired,
    getLeaseDraftConfigApiState: PropTypes.object.isRequired,
    partnerLeaseDraftConfiguration: PropTypes.object.isRequired,
    appUrl: PropTypes.string.isRequired,
  }

  static fetchData(match, inject) {
    const householdListingId = parseInt(match.params.householdListingId, 10);

    return Promise.all([
      LeaseActions.getDraftingInfo(inject, {
        householdListingId,
      }),
      LeaseDocumentActions.getList(inject, {
        householdListingId,
      }),
    ])
      .then(([leaseDraftingInfo]) => {
        if (leaseDraftingInfo && leaseDraftingInfo.partner) {
          return LeaseActions.getPartnerDraftConfiguration(inject, {
            partnerId: leaseDraftingInfo.partner.id,
          });
        }
      });
  }

  static getPageMetadata = () => ({
    pageTitle: 'Lease Draft Helper | Sunroom Rentals',
  })

  constructor(props) {
    super(props);
    this.state = {
      debugMode: false,
      generatingLeaseBusy: false,
      showLeaseDocumentsInfo: false,
    };
  }

  _onToggleDebugMode = () => {
    this.setState({
      debugMode: !this.state.debugMode,
    });
  }

  _onToggleLeaseDocumentsInfo = () => {
    this.setState({
      showLeaseDocumentsInfo: !this.state.showLeaseDocumentsInfo,
    });
  }

  _onCopyMlsClick = () => {
    const { leaseDraftInfo } = this.props;
    copyToClipboard(leaseDraftInfo.listing.mlsNumber);
  }

  _onCopyTitleClick = () => {
    const { leaseDraftInfo } = this.props;
    copyToClipboard(makeListingFullAddressString(leaseDraftInfo.listing));
  }

  _onVoidPendingLeaseDocumentClick = (leaseDocumentId) => {
    const { dispatch, match } = this.props;
    const confirmed = global.confirm('Are you sure you want to void this lease agreement?');
    if (!confirmed) {
      return;
    }
    this._setGeneratingLeaseBusy(true);
    return dispatch(postWithState, {
      action: SR_API_ACTION_LEASE_DOCUMENT_VOID,
      payload: {
        leaseDocumentId,
      },
    })
      .then(() => {
        return dispatch(LeaseDocumentActions.getList, {
          householdListingId: parseInt(match.params.householdListingId, 10),
          refresh: true,
        });
      })
      .then(() => {
        this._setGeneratingLeaseBusy(false);
      })
      .catch(() => {
        this._setGeneratingLeaseBusy(false);
      });
  }

  _onGenerateLeaseDocumentClick = () => {
    const {
      leaseDocuments,
      leaseDraftInfo,
      partnerLeaseDraftConfiguration,
      dispatch,
      match,
    } = this.props;

    const [leaseDocument] = leaseDocuments;

    if (leaseDocument?.status === LEASE_DOCUMENT_STATUS_SIGNED) {
      global.alert('A lease exists and has already been signed. No lease drafts were generated.');
      return;
    }

    const recipientsList = getRecipientsList({
      leaseDraftInfo,
      leaseDraftConfiguration: partnerLeaseDraftConfiguration,
    });

    const propertyManagerRecipients = recipientsList.filter(({ role, email }) => {
      return role.includes('Landlord') && isEmail(email);
    });
    const recipientsWithoutEmails = recipientsList.filter(({ email }) => !email);

    if (propertyManagerRecipients.length === 0) {
      global.alert('The landlord or property manager contact information is missing.');
      return;
    }

    if (recipientsWithoutEmails.length > 0) {
      global.alert(
        'These recipients are missing email address: ' +
        recipientsWithoutEmails.map(({ name }) => `"${name}"`).join(', ')
      );
      return;
    }

    if (leaseDocument && leaseDocument.status !== LEASE_DOCUMENT_STATUS_VOIDED) {
      global.alert(
        'There is a pending lease already. Please void the pending ' +
        'lease before creating a new draft.'
      );
      return;
    }

    const missingLeaseDraftingFieldIds = getLeaseDraftingFields({
      leaseDraftInfo,
      leaseDraftConfiguration: partnerLeaseDraftConfiguration,
    })
      .filter(({ required, value }) => required && !value)
      .map(({ id }) => id);

    let confirmMessage = 'Are you sure you want to generate and send a lease document?';

    if (missingLeaseDraftingFieldIds.length) {
      confirmMessage = `Missing fields: ${missingLeaseDraftingFieldIds.join(', ')}\n\n${confirmMessage}`;
    }
    if (!global.confirm(confirmMessage)) {
      return;
    }

    this._setGeneratingLeaseBusy(true);

    return dispatch(postWithState, {
      action: SR_API_ACTION_LEASE_DOCUMENT_CREATE_DRAFT,
      payload: {
        householdListingId: parseInt(match.params.householdListingId, 10),
      },
    })
      .then((leaseDocument) => {
        return dispatch(postWithState, {
          action: SR_API_ACTION_LEASE_DOCUMENT_REVIEW_DRAFT,
          payload: {
            leaseDocumentId: leaseDocument.id,
          },
        });
      })
      .then((leaseDocument) => {
        return dispatch(postWithState, {
          action: SR_API_ACTION_LEASE_DOCUMENT_REQUEST_SIGNATURE,
          payload: {
            leaseDocumentId: leaseDocument.id,
          },
        });
      })
      .then(() => {
        return dispatch(LeaseDocumentActions.getList, {
          householdListingId: parseInt(match.params.householdListingId, 10),
          refresh: true,
        });
      })
      .then(() => {
        this._setGeneratingLeaseBusy(false);
      })
      .catch(() => {
        this._setGeneratingLeaseBusy(false);
      });
  }

  _setGeneratingLeaseBusy = (generatingLeaseBusy) => {
    this.setState({
      generatingLeaseBusy,
    });
  }

  render() {
    const {
      leaseDraftInfo,
      leaseDocuments,
      partnerLeaseDraftConfiguration,
      getDraftingInfoApiState,
      getLeaseDraftConfigApiState,
      currentUser,
      appUrl,
      dispatch,
      isTransitioning,
      match: { params },
    } = this.props;

    const {
      debugMode,
      generatingLeaseBusy,
      showLeaseDocumentsInfo,
    } = this.state;

    const householdListingId = parseInt(params.householdListingId, 10);
    const [leaseDocument] = leaseDocuments;

    if (
      getDraftingInfoApiState.inProgress
      || getLeaseDraftConfigApiState.inProgress
      || isTransitioning
    ) {
      return <div className='loading-gif' />;
    }

    if (!leaseDraftInfo || !partnerLeaseDraftConfiguration) {
      return (
        <SrErrorMessage>
          Can not find the lease draft information for:<br /><br />
          Household Listing ID: {householdListingId}<br />
        </SrErrorMessage>
      );
    }

    const mlsListingLink = leaseDraftInfo.listingLinks?.find(({ siteName }) => siteName === 'MLS');

    const links = [
      {
        title: 'Edit Draft Config',
        linkTo: url.resolve(appUrl, '/admin/lease-draft-config'),
      },
      {
        title: 'Household Card',
        linkTo: url.resolve(appUrl, `/admin/household/all/${leaseDraftInfo.leadRenterId}`),
      },
      {
        title: 'MLS',
        linkTo: mlsListingLink?.linkUrl,
      },
      {
        title: 'Partner Listing',
        // eslint-disable-next-line max-len
        linkTo: url.resolve(appUrl, `/partner/${leaseDraftInfo.partner.slug}/listing/${leaseDraftInfo.listing.id}`),
      },
      {
        title: 'Renter Listing',
        linkTo: url.resolve(appUrl, `/listing/${leaseDraftInfo.listing.id}`),
      },
      {
        title: 'CLP',
        linkTo: url.resolve(appUrl, `/admin/listings/edit/${leaseDraftInfo.listing.id}`),
      },
    ];

    return (
      <SrBox backgroundColor='#F7F7F7'>
        <SrBox padding='0px 30px 30px' maxWidth='1440px' margin='0 auto'>
          <SrBox
            backgroundColor='#F7F7F7'
            height='90px'
            paddingTop='33px'
            position='sticky'
            zIndex='1'
            top='0'
          >
            <SrFlex align='start' wrap='nowrap' justify='between'>
              <div>
                <SrText
                  font='greycliff'
                  display='block'
                  fontSize='28px'
                  weight='700'
                  cursor='pointer'
                  onClick={this._onCopyTitleClick}
                >
                  {makeListingFullAddressString(leaseDraftInfo.listing)}
                </SrText>
                <SrBox padding='8px 0' onClick={this._onCopyMlsClick}>
                  <SrText font='greycliff' fontSize='18px' weight='600' color='grey' cursor='pointer'>
                    {'MLS ID: '}
                  </SrText>
                  <SrText font='greycliff' fontSize='18px' weight='600' color='black' cursor='pointer'>
                    {leaseDraftInfo.listing.mlsNumber}
                  </SrText>
                </SrBox>
              </div>
              <div>
                {links.filter(({ linkTo }) => linkTo).map(({ title, linkTo }) => (
                  <SrBox key={title} paddingLeft='40px' display='inlineBlock'>
                    <SrLink target='_blank' to={linkTo}>
                      <SrText font='greycliff' fontSize='14px' weight='700' color='blue300'>
                        {title}
                      </SrText>
                    </SrLink>
                  </SrBox>
                ))}
              </div>
            </SrFlex>
          </SrBox>
          <SrFlex align='start' wrap='nowrap' justify='between'>
            <div style={{ width: '66%', paddingRight: '20px' }}>
              {leaseDraftInfo.partner.enableLeaseDraftingIntegration &&
                <>
                  {(!leaseDocument || leaseDocument.status === LEASE_DOCUMENT_STATUS_VOIDED) &&
                    <SrButton
                      theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                      onClick={!generatingLeaseBusy
                        ? this._onGenerateLeaseDocumentClick
                        : null
                      }
                    >
                      {generatingLeaseBusy
                        ? <SrLoadingSpinner width='20px' height='20px' />
                        : 'Generate Lease'
                      }
                    </SrButton>
                  }
                  {leaseDocument && leaseDocument.status !== LEASE_DOCUMENT_STATUS_VOIDED &&
                    <SrButton
                      theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                      onClick={!generatingLeaseBusy
                        ? () => this._onVoidPendingLeaseDocumentClick(leaseDocument.id)
                        : null
                      }
                    >
                      {generatingLeaseBusy
                        ? <SrLoadingSpinner width='20px' height='20px' />
                        : 'Void Pending Lease'
                      }
                    </SrButton>
                  }
                  {leaseDocuments.length > 0 && (
                    <>
                      <SrText
                        onClick={this._onToggleLeaseDocumentsInfo}
                        font='greycliff'
                        fontSize='14px'
                        weight='700'
                        color='blue300'
                        cursor='pointer'
                      >
                        {showLeaseDocumentsInfo ? 'Hide raw lease code' : 'Show raw lease code'}
                      </SrText>
                      {showLeaseDocumentsInfo &&
                        <pre>
                          {JSON.stringify(leaseDocuments, null, 2)}
                        </pre>
                      }
                    </>
                  )}
                </>
              }
              <SrLeaseDraftHelper
                leaseDraftInfo={leaseDraftInfo}
                leaseDraftConfiguration={partnerLeaseDraftConfiguration}
              />
            </div>
            <div style={{ width: '34%', position: 'sticky', top: '123px' }}>
              <SrAdminHouseholdNotes
                householdId={leaseDraftInfo.id}
                dispatch={dispatch}
                currentUser={currentUser}
              />
            </div>
          </SrFlex>
        </SrBox>
        <SrBox padding='0px 30px 30px' maxWidth='1440px' margin='0 auto'>
          <SrText
            onClick={this._onToggleDebugMode}
            font='greycliff'
            fontSize='14px'
            weight='700'
            color='blue300'
            cursor='pointer'
          >
            {debugMode ? 'Hide raw data' : 'Show raw data'}
          </SrText>
          {debugMode && (
            <pre>
              {JSON.stringify({ leaseDraftInfo, partnerLeaseDraftConfiguration }, null, 2)}
            </pre>
          )}
        </SrBox>
      </SrBox>
    );
  }
}

AdminLeaseDraftHelperPage.displayName = 'AdminLeaseDraftHelperPage';

export default AdminLeaseDraftHelperPage;
